import { firebaseAuth, API_BASE_URL } from "./firebase";

async function authedFetch(path: string, body: unknown, method = "POST") {
  const token = firebaseAuth?.currentUser
    ? await firebaseAuth.currentUser.getIdToken()
    : null;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data;
}

async function authedRequest(path: string, opts: {
  method?: string;
  body?: unknown;
  query?: Record<string, string | number | undefined>;
} = {}) {
  const token = firebaseAuth?.currentUser
    ? await firebaseAuth.currentUser.getIdToken()
    : null;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  let url = `${API_BASE_URL}${path}`;
  if (opts.query) {
    const qs = Object.entries(opts.query)
      .filter(([, v]) => v !== undefined && v !== null && v !== "")
      .map(
        ([k, v]) =>
          `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`
      )
      .join("&");
    if (qs) url += `?${qs}`;
  }

  const res = await fetch(url, {
    method: opts.method || "GET",
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data;
}

async function publicFetch(path: string, body: unknown) {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data;
}

// --- Public registration ---

export type RegisterPayload = {
  interviewee: { first_name: string; last_name: string; phone: string };
  orderer: { first_name: string; last_name: string; email: string };
  target_group: string[];
  person_info: string;
  life_events: string;
  website?: string; // honeypot
  // Phase 1 onboarding extras (optional, stored verbatim server-side)
  giftee_relation?: string;
  call_slot?: string;
  biographer?: string;
  gift_birthdate?: string;
  // Friends & Family access code (required during closed beta).
  code?: string;
  // Phase A v2 onboarding extras
  package?: string;
  gift_date?: string;
  gift_occasion?: string;
  payment_method?: string;
  // Legal — AGB consent checkbox on step 8.
  agb_consent?: boolean;
  // Phone-AI warmth fields (Option B optional step-4 extension).
  // Free text except hearing_ability which is one of
  // "gut" | "leicht_eingeschraenkt" | "stark_eingeschraenkt".
  preferred_address?: string;
  hearing_ability?: string;
  dialect_preference?: string;
  living_situation?: string;
  current_mood_note?: string;
  family_events_note?: string;
};

export type CheckCodeResponse = {
  valid: boolean;
  discount_percent?: number;
  message?: string;
};

export async function checkCode(code: string): Promise<CheckCodeResponse> {
  return publicFetch("/codes/check", { code });
}

export async function registerOrder(payload: RegisterPayload) {
  return publicFetch("/register", payload);
}

// --- Auth (public) ---

export async function requestMagicLink(email: string) {
  return publicFetch("/auth/request-link", { email });
}

export async function verifyMagicLink(token: string): Promise<{
  customToken: string;
  email: string;
}> {
  return publicFetch("/auth/verify-link", { token });
}

export async function requestLoginCode(email: string) {
  return publicFetch("/auth/request-code", { email });
}

export async function verifyLoginCode(
  email: string,
  code: string
): Promise<{ customToken: string; email: string }> {
  return publicFetch("/auth/verify-code", { email, code });
}

// --- Customer-scoped /me APIs ---

export type ProfileSummary = {
  phone: string;
  interviewee_name: string;
  target_group: string[];
  createdAt?: any;
  lastCallAt?: any;
  chapters_with_content: number;
  welcome_email_sent: boolean;
};

export type ProfilesResponse = {
  profiles: ProfileSummary[];
  callNumber: string;
};

export async function listProfiles(): Promise<ProfilesResponse> {
  return authedFetch("/me/profiles", {});
}

export type FullProfile = {
  phone: string;
  interviewee_first_name: string;
  interviewee_last_name: string;
  orderer_first_name: string;
  orderer_last_name: string;
  orderer_email: string;
  target_group: string[];
  stammdaten: Record<string, any>;
  profileSummary: string;
  open_questions: {
    verständnis_fragen: string[];
    vertiefungsfragen: string[];
    biografie_fragen: string[];
  };
  key_people: string[];
  sensitivities: string;
  chapters_to_exclude: string;
  callNumber: string;
  // Gift onboarding (all optional — empty string if not collected)
  giftee_relation: string;
  package: string;
  gift_date: string;
  gift_occasion: string;
  gift_birthdate: string;
  person_info: string;
  life_events: string;
  call_slot: string;
  biographer: string;
  payment_method: string;
  // Code & discount
  friends_family_code: string;
  code_discount_percent: number;
  code_source: string;
  is_friends_family: boolean;
  // Legal
  agb_consent: boolean;
  agb_consent_at: string | null;
  // Phone-AI warmth (all optional, may be empty strings)
  preferred_address: string;
  hearing_ability: string;
  dialect_preference: string;
  living_situation: string;
  current_mood_note: string;
  family_events_note: string;
  // Lifecycle
  createdAt: string | null;
  updatedAt: string | null;
  welcome_email_sent: boolean;
};

export async function getProfile(phone: string): Promise<FullProfile> {
  return authedFetch("/me/profile", { phone });
}

// Records a right-of-withdrawal (Widerruf) server-side and triggers the
// confirmation + team notification emails.
export async function requestWiderruf(
  phone?: string,
  reason?: string,
): Promise<{ success: boolean; confirmation_email_sent: boolean }> {
  return authedFetch("/me/widerruf", {
    phone: phone || undefined,
    reason: reason || undefined,
  });
}

export type ChapterSummary = {
  title: string;
  summary: string;
  content_preview: string;
  content_length: number;
  saturation_score: number;
  exported: boolean;
  pending_goals: string[];
};

export async function listChapters(phone: string): Promise<{ chapters: ChapterSummary[] }> {
  return authedFetch("/me/chapters", { phone });
}

export type ChapterDetail = {
  title: string;
  content: string;
  summary: string;
  saturation_score: number;
  exported: boolean;
  pending_goals: string[];
  analyses: string[];
};

export async function getChapter(phone: string, chapterTitle: string): Promise<ChapterDetail> {
  return authedFetch("/me/chapter", { phone, chapterTitle });
}

export type TranscriptItem = {
  transcriptId: string;
  timestamp: any;
  processed: boolean;
  processedAt?: any;
  duration_seconds?: number;
  preview?: string;
  length?: number;
  transcript?: string;
};

export async function listTranscripts(
  phone: string,
  opts?: { includeText?: boolean }
): Promise<{ transcripts: TranscriptItem[] }> {
  return authedFetch("/me/transcripts", {
    phone,
    includeText: !!opts?.includeText,
  });
}

export type Settings = {
  sensitivities?: string;
  chapters_to_exclude?: string;
  key_people?: string[];
  target_group?: string[];
  interviewer_tone?: string;
  tone?: string;
  dialekt?: string;
  icebreaker?: string;
  // Phone-AI warmth (editable from /app/storyteller)
  preferred_address?: string;
  hearing_ability?: string;
  dialect_preference?: string;
  living_situation?: string;
  current_mood_note?: string;
  family_events_note?: string;
};

export type BookMeta = {
  has_book: boolean;
  google_doc_id: string;
  chapter_count_with_content: number;
  chapter_count_total: number;
  chapters: {
    title: string;
    word_count: number;
    exported: boolean;
    saturation_score: number;
  }[];
  total_words: number;
  estimated_pages: number;
  estimated_kb: number;
  last_call_date: string | null;
  updated_at: string | null;
  interviewee_first_name: string;
  interviewee_last_name: string;
};

export async function getBookMeta(phone: string): Promise<BookMeta> {
  return authedFetch("/me/book", { phone });
}

/**
 * Triggers a PDF download of the user's Google Doc. Resolves once the
 * browser has the bytes; rejects (throws) if the server returns an error.
 * The download is initiated via a temporary anchor + Object URL so the
 * filename suggested by the server's Content-Disposition is preserved.
 */
export async function downloadBookPdf(phone: string): Promise<void> {
  const token = firebaseAuth?.currentUser
    ? await firebaseAuth.currentUser.getIdToken()
    : null;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${API_BASE_URL}/me/book/pdf`, {
    method: "POST",
    headers,
    body: JSON.stringify({ phone }),
  });
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error || j?.message || msg;
    } catch {
      /* binary response */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match?.[1] || "Vitanara_Band-I.pdf";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

// --- /me/family --------------------------------------------------------------

export type FamilyRole = "vollzugriff" | "lesen_hoeren" | "lesen";

export type FamilyMember = {
  email: string;
  name: string;
  role: FamilyRole;
  status: string;
  invited_by: string;
  invited_at: string | null;
};

export type FamilyListResponse = {
  members: FamilyMember[];
  storyteller: { first_name: string; last_name: string };
  orderer_email: string;
};

export async function listFamily(phone: string): Promise<FamilyListResponse> {
  return authedFetch("/me/family", { phone });
}

export async function inviteFamilyMember(
  phone: string,
  email: string,
  role: FamilyRole,
  name?: string,
): Promise<{ success: boolean; email: string; role: FamilyRole; email_sent: boolean }> {
  return authedFetch("/me/family/invite", { phone, email, role, name });
}

export async function updateFamilyRole(
  phone: string,
  email: string,
  role: FamilyRole,
): Promise<{ success: boolean }> {
  return authedFetch("/me/family/update", { phone, email, role });
}

export async function removeFamilyMember(
  phone: string,
  email: string,
): Promise<{ success: boolean }> {
  return authedFetch("/me/family/remove", { phone, email });
}

// --- /me/photos --------------------------------------------------------------

export type Photo = {
  id: string;
  caption: string;
  mime_type: string;
  size_bytes: number;
  storage_path: string;
  download_url: string | null;
  created_at: string | null;
  uploaded_by: string;
};

export async function listPhotos(phone: string): Promise<{ photos: Photo[] }> {
  return authedFetch("/me/photos", { phone });
}

export async function uploadPhoto(
  phone: string,
  file: File,
  caption?: string,
): Promise<{ success: boolean; photo: Photo }> {
  const token = firebaseAuth?.currentUser
    ? await firebaseAuth.currentUser.getIdToken()
    : null;
  const fd = new FormData();
  fd.append("phone", phone);
  if (caption) fd.append("caption", caption);
  fd.append("file", file);
  const res = await fetch(`${API_BASE_URL}/me/photos/upload`, {
    method: "POST",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
    body: fd,
  });
  let data: any = null;
  try {
    data = await res.json();
  } catch {
    /* empty body */
  }
  if (!res.ok) {
    const message = (data && (data.error || data.message)) || `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data;
}

export async function updatePhotoCaption(
  phone: string,
  id: string,
  caption: string,
): Promise<{ success: boolean }> {
  return authedFetch("/me/photos/caption", { phone, id, caption });
}

export async function deletePhoto(
  phone: string,
  id: string,
): Promise<{ success: boolean }> {
  return authedFetch("/me/photos/delete", { phone, id });
}

export async function updateSettings(phone: string, settings: Settings) {
  return authedFetch("/me/update-settings", { phone, settings });
}

// --- Admin Auth ---

export async function requestAdminMagicLink(email: string) {
  return publicFetch("/auth/request-link", { email, mode: "admin" });
}

export async function verifyAdminMagicLink(token: string): Promise<{
  customToken: string;
  email: string;
  mode: string;
}> {
  return publicFetch("/admin/auth/verify-link", { token });
}

// --- Admin API ---

export type AdminUserRow = {
  phone: string;
  interviewee_first_name: string;
  interviewee_last_name: string;
  interviewee_name: string;
  orderer_name: string;
  orderer_email: string;
  target_group: string[];
  createdAt?: any;
  updatedAt?: any;
  lastCallAt?: any;
  chapter_count: number;
  call_count: number;
  welcome_email_sent: boolean;
  source: string;
};

export type AdminListUsersResponse = {
  users: AdminUserRow[];
  total: number;
  limit: number;
  offset: number;
};

export async function adminListUsers(opts?: {
  search?: string;
  limit?: number;
  offset?: number;
}): Promise<AdminListUsersResponse> {
  return authedRequest("/admin/users", {
    method: "GET",
    query: {
      search: opts?.search,
      limit: opts?.limit,
      offset: opts?.offset,
    },
  });
}

export type AdminUserDoc = {
  phone: string;
  data: Record<string, any>;
};

export async function adminGetUser(phone: string): Promise<AdminUserDoc> {
  return authedRequest(`/admin/users/${encodeURIComponent(phone)}`, {
    method: "GET",
  });
}

export async function adminUpdateUser(
  phone: string,
  patch: Record<string, any>
): Promise<{ success: boolean; updated: string[]; rejected: string[] }> {
  return authedRequest(`/admin/users/${encodeURIComponent(phone)}`, {
    method: "PATCH",
    body: patch,
  });
}

export type AdminCreateUserPayload = {
  interviewee: { first_name: string; last_name: string; phone: string };
  orderer: { first_name: string; last_name: string; email: string };
  target_group: string[];
  person_info: string;
  life_events: string;
};

export async function adminCreateUser(
  payload: AdminCreateUserPayload
): Promise<{ success: boolean; phone: string }> {
  return authedRequest("/admin/users", {
    method: "POST",
    body: payload,
  });
}

export async function adminDeleteUser(
  phone: string
): Promise<{ success: boolean; deleted_subcollections: string[] }> {
  return authedRequest(`/admin/users/${encodeURIComponent(phone)}`, {
    method: "DELETE",
  });
}

export async function adminResendWelcome(
  phone: string
): Promise<{ success: boolean }> {
  return authedRequest(
    `/admin/users/${encodeURIComponent(phone)}/resend-welcome`,
    { method: "POST", body: {} }
  );
}

export async function adminListChapters(
  phone: string
): Promise<{ chapters: ChapterSummary[] }> {
  return authedRequest(
    `/admin/users/${encodeURIComponent(phone)}/chapters`,
    { method: "GET" }
  );
}

export async function adminGetChapter(
  phone: string,
  chapterId: string
): Promise<ChapterDetail> {
  return authedRequest(
    `/admin/users/${encodeURIComponent(phone)}/chapters/${encodeURIComponent(chapterId)}`,
    { method: "GET" }
  );
}

export type AdminCall = {
  transcriptId: string;
  timestamp: any;
  processed: boolean;
  processedAt?: any;
  duration_seconds?: number;
  preview: string;
  length: number;
};

export async function adminListCalls(
  phone: string
): Promise<{ calls: AdminCall[] }> {
  return authedRequest(
    `/admin/users/${encodeURIComponent(phone)}/calls`,
    { method: "GET" }
  );
}

export async function adminGetBookMeta(phone: string): Promise<BookMeta> {
  return authedRequest(`/admin/users/${encodeURIComponent(phone)}/book`);
}

/**
 * Admin-scoped variant of downloadBookPdf — uses the admin's ID token
 * (which already carries the `admin: true` claim) and the /admin endpoint
 * so the test does not require the customer's login.
 */
export async function adminDownloadBookPdf(phone: string): Promise<void> {
  const token = firebaseAuth?.currentUser
    ? await firebaseAuth.currentUser.getIdToken()
    : null;
  const headers: Record<string, string> = {};
  if (token) headers["Authorization"] = `Bearer ${token}`;
  const res = await fetch(
    `${API_BASE_URL}/admin/users/${encodeURIComponent(phone)}/book/pdf`,
    { method: "GET", headers },
  );
  if (!res.ok) {
    let msg = `HTTP ${res.status}`;
    try {
      const j = await res.json();
      msg = j?.error || j?.message || msg;
    } catch {
      /* binary */
    }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const disposition = res.headers.get("Content-Disposition") || "";
  const match = /filename="?([^"]+)"?/i.exec(disposition);
  const filename = match?.[1] || "Vitanara_Band-I.pdf";
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export async function adminDeleteCall(
  phone: string,
  callId: string
): Promise<{ success: boolean }> {
  return authedRequest(
    `/admin/users/${encodeURIComponent(phone)}/calls/${encodeURIComponent(callId)}`,
    { method: "DELETE" }
  );
}

// --- Admin: manage other admins ---

export type AdminAccount = {
  email: string;
  source: "env" | "firestore";
  added_at: string | null;
  added_by: string | null;
  note: string | null;
};

export type AdminListAdminsResponse = {
  admins: AdminAccount[];
  current_admin: string;
};

export async function adminListAdmins(): Promise<AdminListAdminsResponse> {
  return authedRequest(`/admin/admins`);
}

export async function adminAddAdmin(
  email: string,
  note?: string
): Promise<{ success: boolean; email: string; source: string; duplicate?: boolean }> {
  return authedRequest(`/admin/admins`, {
    method: "POST",
    body: { email, note: note || "" },
  });
}

export async function adminRemoveAdmin(
  email: string
): Promise<{ success: boolean; email: string }> {
  return authedRequest(
    `/admin/admins/${encodeURIComponent(email)}`,
    { method: "DELETE" }
  );
}

// --- Admin: Friends & Family code management ---

export type CodeAccount = {
  code: string;
  discount_percent: number;
  max_uses: number;
  uses_count: number;
  active: boolean;
  note: string;
  created_by: string | null;
  created_at: string | null;
  expires_at: string | null;
  last_used_at: string | null;
  source: "firestore" | "env";
};

export async function adminListCodes(): Promise<{ codes: CodeAccount[] }> {
  return authedRequest(`/admin/codes`);
}

export async function adminCreateCode(payload: {
  code: string;
  discount_percent: number;
  max_uses: number;
  note?: string;
  expires_at?: string;
  active?: boolean;
}): Promise<{ success: boolean; code: CodeAccount }> {
  return authedRequest(`/admin/codes`, { method: "POST", body: payload });
}

export async function adminUpdateCode(
  code: string,
  patch: Partial<{
    discount_percent: number;
    max_uses: number;
    note: string;
    expires_at: string | null;
    active: boolean;
  }>
): Promise<{ success: boolean; code: CodeAccount }> {
  return authedRequest(`/admin/codes/${encodeURIComponent(code)}`, {
    method: "PATCH",
    body: patch,
  });
}

export async function adminDeleteCode(
  code: string
): Promise<{ success: boolean; code: string }> {
  return authedRequest(`/admin/codes/${encodeURIComponent(code)}`, {
    method: "DELETE",
  });
}
